import { paths } from '@common/types';
import { Avatar, Box, Divider, Tooltip, Typography } from '@mui/material';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { ProfileAvatar } from 'components/ProfileAvatar';
import { Phone, Print, Email, Language, LocationOn } from '@mui/icons-material';
import { ClipboardButton } from 'components/ClipboardButton';
import { useDetailsCard } from './hooks';
import { EditLocation } from './components/EditLocation';
import { EditContactInformation } from './components/EditContactInformation';
import { theme } from '@store';
import { t } from 'i18next';
import { StatusBanner, TruncatedTextWithConditionalTooltip } from '@components';
import { ClinicCaption } from '@views/pages/Patient/components/DetailsCard/components/ClinicCaption';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';

interface IDetailsCardProps {
  patient: paths[`/patient/{patient_id}`]['get']['responses']['200']['content']['application/json'];
  isEditDisabled: boolean;
}

export const DetailsCard = ({ patient, isEditDisabled }: IDetailsCardProps) => {
  const {
    handleNavigateToAllPatients,
    caregiver,
    isEmailOverflowing,
    contactPersonIsCaregiver,
    currentFormattedDate,
  } = useDetailsCard(patient);

  return (
    <Box
      sx={{
        width: '400px',
        padding: '1.5rem',
        boxShadow: 'initial',
        backgroundColor: 'white',
        border: '1px solid rgba(0, 0, 0, 0.12)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Typography
          variant='body2'
          sx={{ color: theme => theme.palette.semiBlack.main }}
        >
          ID: {patient.display_id}
        </Typography>
      </Box>

      <ClinicCaption
        patient_id={patient.id}
        clinical_caption={patient.clinical_caption}
        disabled={isEditDisabled}
      />

      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Section header */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: '0.25rem',
          }}
        >
          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <Typography variant='subtitle2'>Contact information</Typography>
            {patient.do_not_call && (
              <Box
                sx={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}
              >
                <PhoneDisabledIcon
                  sx={{
                    fontSize: '16px',
                  }}
                  htmlColor={theme.palette.error.main}
                />
                <Typography
                  variant='caption'
                  sx={{ color: theme => theme.palette.error.main }}
                >
                  Do not call
                </Typography>
              </Box>
            )}
          </Box>

          <EditContactInformation
            patient_id={patient.id}
            disabled={isEditDisabled}
          />
        </Box>
        {/* Section body */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            {caregiver?.first_name && contactPersonIsCaregiver && (
              <>
                <TruncatedTextWithConditionalTooltip
                  width='280px'
                  variant='body2'
                  sx={{ fontSize: '14px' }}
                  text={`${caregiver.first_name} ${caregiver.middle_name} ${caregiver.last_name} (${caregiver.relationship})`}
                />
                <Typography
                  variant='caption'
                  sx={{ color: 'text.disabled', fontSize: '12px' }}
                >
                  {t('patient.detailsCard.caregiver')}
                </Typography>
              </>
            )}
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <Email
                sx={{ width: '16px', height: '16px' }}
                htmlColor={theme.palette.semiBlack.main}
              />
              <Tooltip
                title={
                  isEmailOverflowing ? patient.contact_information.email : ''
                }
                arrow
                placement='top'
              >
                <Typography
                  variant='body1'
                  sx={{
                    maxWidth: '14rem',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    cursor: isEmailOverflowing ? 'pointer' : 'default',
                    fontSize: '14px',
                  }}
                >
                  {patient.contact_information.email || ' - '}
                </Typography>
              </Tooltip>
              <ClipboardButton
                textToCopy={patient.contact_information.email}
                size={16}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: '0.5rem',
                minWidth: 'fit-content',
              }}
            >
              {!patient.do_not_call && (
                <>
                  <Phone
                    sx={{ width: '16px', height: '16px' }}
                    htmlColor={theme.palette.semiBlack.main}
                  />
                  <Typography variant='body1' sx={{ fontSize: '14px' }}>
                    {patient.contact_information.phone_number || ' - '}
                  </Typography>
                  <ClipboardButton
                    textToCopy={patient.contact_information.phone_number}
                    size={16}
                  />
                </>
              )}
            </Box>
            {(patient.contact_information.language !== 'English' ||
              caregiver?.language !== 'English') && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Language
                  sx={{ width: '16px', height: '16px' }}
                  htmlColor={theme.palette.semiBlack.main}
                />
                <Typography variant='body2' sx={{ fontSize: '14px' }}>
                  {caregiver?.language && contactPersonIsCaregiver
                    ? caregiver?.language
                    : patient.contact_information.language}
                </Typography>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: 'fit-content',
              gap: '1.5rem',
            }}
          >
            <Divider orientation='vertical' />
            {patient.contact_information.phone_number &&
            !patient.do_not_call ? (
              <a
                href={`https://dialpad.com/launch?phone=${patient.contact_information.phone_number.replace(/[^\d+]/g, '')}`}
                // target='_blank'
                rel='noopener noreferrer'
                style={{ display: 'inline-flex', textDecoration: 'none' }}
              >
                <Phone color='primary' />
              </a>
            ) : (
              <Phone color='disabled' />
            )}
          </Box>
        </Box>
      </Box>

      <Divider orientation='horizontal' sx={{ my: '0.5rem' }} />

      {patient?.outside_business_hours && (
        <StatusBanner
          text={`Based on patient location it is ${currentFormattedDate}`}
          variant={'lightWarning'}
        />
      )}

      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Section header */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            my: '0.25rem',
          }}
        >
          <Typography variant='subtitle2'>Location</Typography>
          <EditLocation patient_id={patient.id} disabled={isEditDisabled} />
        </Box>

        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
          <Tooltip
            disableHoverListener={patient.location.lives_at_home}
            disableFocusListener={patient.location.lives_at_home}
            title={patient.location.address_line}
          >
            {patient.location.lives_at_home ? (
              <Typography variant='body2'>
                {patient.location.address_line}
              </Typography>
            ) : (
              <Box
                onClick={() =>
                  handleNavigateToAllPatients(patient.location.building_id)
                }
                sx={{ cursor: 'pointer' }}
              >
                <Typography
                  variant='body2'
                  sx={{ textDecoration: 'underline', color: 'primary.main' }}
                >
                  {patient.location.building_name}
                </Typography>
              </Box>
            )}
          </Tooltip>
          <Typography
            variant='caption'
            sx={{ color: theme => theme.palette.semiBlack.main }}
          >
            {patient.location.lives_at_home ? 'Home' : 'Building'}
          </Typography>
        </Box>

        {/* Section body */}
        {!patient.location.lives_at_home && (
          <>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <Phone
                    sx={{ width: '16px', height: '16px' }}
                    htmlColor={theme.palette.semiBlack.main}
                  />
                  <Typography variant='body1' sx={{ fontSize: '14px' }}>
                    {patient.location.phone_number
                      ? patient.location.phone_number
                      : '-'}
                  </Typography>
                  {patient.location.phone_number && (
                    <ClipboardButton
                      textToCopy={patient.location.fax_number}
                      size={16}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <Print
                    sx={{ width: '16px', height: '16px' }}
                    htmlColor={theme.palette.semiBlack.main}
                  />
                  <Typography variant='body1' sx={{ fontSize: '14px' }}>
                    {patient.location.fax_number || ' - '}
                  </Typography>
                  {patient.location.fax_number && (
                    <ClipboardButton
                      textToCopy={patient.location.fax_number}
                      size={16}
                    />
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: 'fit-content',
                  gap: '1.5rem',
                }}
              >
                <Divider orientation='vertical' />
                {patient.location.phone_number ? (
                  <a
                    href={`https://dialpad.com/launch?phone=${patient.location.phone_number.replace(/[^\d+]/g, '')}`}
                    // target='_blank'
                    rel='noopener noreferrer'
                    style={{ display: 'inline-flex', textDecoration: 'none' }}
                  >
                    <Phone color='primary' />
                  </a>
                ) : (
                  <Phone color='disabled' />
                )}
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: '0.5rem',
                marginY: 1,
              }}
            >
              <LocationOn
                sx={{ width: '16px', height: '16px' }}
                htmlColor={theme.palette.semiBlack.main}
              />
              <TruncatedTextWithConditionalTooltip
                variant='body2'
                text={patient.location.address_line || '-'}
                width='250px'
              />
            </Box>
          </>
        )}
      </Box>

      <Divider orientation='horizontal' sx={{ my: '0.5rem' }} />

      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Section header */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            my: '0.25rem',
          }}
        >
          <Typography variant='subtitle2'>Care team</Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {patient.care_team.practice_thumbnail_xsmall ? (
            <ProfileAvatar
              encoded_image={patient.care_team.practice_thumbnail_xsmall}
              firstName={''}
              lastName={''}
            />
          ) : (
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
              <MedicalServicesIcon
                aria-label='Default practice icon'
                sx={{ width: 18, height: 18 }}
              />
            </Avatar>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              pl: '1rem',
              width: 'calc(100% - 40px)',
            }}
          >
            <TruncatedTextWithConditionalTooltip
              width='100%'
              text={patient.care_team.practice_name}
              variant='body1'
              sx={{ fontSize: '14px' }}
            />
            <TruncatedTextWithConditionalTooltip
              width='100%'
              text={patient.care_team.partner_name}
              variant='body2'
              sx={{
                color: theme => theme.palette.semiBlack.main,
                fontSize: '12px',
              }}
            />
          </Box>
        </Box>

        {/* Section body */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              flexDirection: 'column',
              mt: 0.5,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <Typography
                variant='body2'
                sx={{ color: theme.palette.semiBlack.main }}
              >
                Internal PCP:&nbsp;
              </Typography>
              <TruncatedTextWithConditionalTooltip
                width='100%'
                text={`${
                  (!!patient.care_team.provider_id &&
                    patient.care_team.provider_name) ||
                  ' - '
                } ${patient.care_team.provider_inactive ? '(Inactive)' : ''}`}
                variant='body2'
                color={
                  patient.care_team.provider_inactive
                    ? 'text.disabled'
                    : 'text.primary'
                }
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <Typography
                variant='body2'
                sx={{ color: theme.palette.semiBlack.main }}
              >
                Region:&nbsp;
              </Typography>
              <TruncatedTextWithConditionalTooltip
                width='100%'
                text={`${patient.care_team.provider_region || ' - '}`}
                variant='body2'
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <Typography
                variant='body2'
                sx={{ color: theme.palette.semiBlack.main }}
              >
                Care navigator:&nbsp;
              </Typography>
              <TruncatedTextWithConditionalTooltip
                width='65%'
                text={`${patient.care_team.care_navigator_name || ' - '} (${patient.care_team.care_navigator_team_name || ' - '})`}
                variant='body2'
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <Typography
                variant='body2'
                sx={{ color: theme.palette.semiBlack.main }}
              >
                Care manager:&nbsp;
              </Typography>
              <TruncatedTextWithConditionalTooltip
                width='65%'
                text={`${patient.care_team.care_manager_name || ' - '}`}
                variant='body2'
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
